import { Component, DestroyRef, effect, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { NavigationStart, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AccountCardComponent } from './components/account-card/account-card.component';
import { MatIconModule } from '@angular/material/icon';
import { HasPermissionDirective } from '@olv/ui-shared/directives/has-permission.directive';
import { AccountStatus, Account, AccountFilters, Group } from '@olv/ui-shared/models/account.interface';
import { Resources, Scopes } from '@olv/ui-shared/models/settlement-permissions.model';
import { getRoleNameMap } from '@olv/ui-shared/models/role.constants';
import { AccountService } from '@olv/ui-shared/services/account.service';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { AuthService, UserProfile } from '@olv/ui-shared/services/auth.service';
import { PillDropdownComponent } from '@olv/ui-shared/ui-shared/pill-dropdown/pill-dropdown.component';
import { PillSearchComponent } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VirtualScrollComponent } from '@olv/ui-shared/ui-shared/virtual-scroll/virtual-scroll.component';
import { ButtonComponent } from '@olv/ui-shared/ui-shared/button/button.component';
import { AccountFilterBase } from './account-filter.base';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';

@Component({
  selector: 'app-account-management',
  standalone: true,
  imports: [
    FormsModule,
    AccountCardComponent,
    PillDropdownComponent,
    PillSearchComponent,
    HasPermissionDirective,
    MatIconModule,
    LoadingSpinnerComponent,
    VirtualScrollComponent,
    NgxSkeletonLoaderComponent,
    ButtonComponent,
  ],
  templateUrl: './account-management.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./account-management.component.scss'],
})
export class AccountManagementComponent extends AccountFilterBase implements OnInit {
  private readonly appCtx = inject(AppContextService);

  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;
  private accountService = inject(AccountService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  // Signal-based reactive state
  readonly accounts = this.accountService.filteredAccounts;
  readonly totalCount = this.accountService.totalCount;
  readonly loading = this.accountService.loading;
  readonly filters = this.accountService.filters;

  // Local component state
  entityLabel!: 'User' | 'Account';
  pageSize = 10;

  // Current user info
  currentUser: Account | null = null;
  activeUser: UserProfile | null = null;
  isLoadingCurrentUser = false;
  initializing = true;

  constructor() {
    super();
    effect(() => {
      const updated = this.accountService.profileUpdated();
      if (updated) {
        this.loadCurrentUser();
        this.loadAccounts();
      }
    });
  }

  ngOnInit(): void {
    const appName = this.appCtx.getAppName();
    this.entityLabel = appName === 'merchant' ? 'User' : 'Account';

    const filters = this.accountService.filters();
    if (!filters.status) {
      const defaultStatusOption = this.statusOptions.find((o) => o.value === this.DEFAULT_STATUS);
      if (defaultStatusOption) {
        this.selectedStatusOption = defaultStatusOption.label;
        this.accountService.setFilters({ status: defaultStatusOption.value });
      }
    } else {
      this.loadFilters();
    }

    this.loadRoles();

    this.authService.currentUser$.subscribe((user) => {
      this.activeUser = user;
      this.loadCurrentUser();
    });

    this.listenRouteLeave();
  }

  private loadCurrentUser(): void {
    const username = this.activeUser?.username;
    if (!username) {
      console.error('No active username available');
      return;
    }

    this.isLoadingCurrentUser = true;

    this.accountService.getAccountProfile().subscribe({
      next: (account) => {
        this.currentUser = {
          ...account,
          isCurrentUser: true,
        };
        this.isLoadingCurrentUser = false;
      },
      error: (error) => {
        console.error('Failed to load current user by username:', error);
        this.isLoadingCurrentUser = false;
      },
    });
  }

  private loadRoles(): void {
    this.accountService.getAccountGroups().subscribe({
      next: (groups) => {
        const roleNameMap = getRoleNameMap(this.appCtx.getAppName());

        const groupOptions = groups.map((group) => ({
          label: group.name,
          value: roleNameMap[group.name],
        }));

        groupOptions.sort((a, b) => {
          const orderA = a.value;
          const orderB = b.value;
          return orderA - orderB;
        });

        this.roleOptions = [...groupOptions];

        const filters = this.accountService.filters();
        if (filters.role) {
          const role = this.roleOptions.find((o) => o.value === filters.role);
          if (role) {
            this.selectedRoleOption = role.label;
          }
        }

        this.initializeAccounts();
      },
      error: (err) => {
        console.error('Failed to load account roles:', err);
        this.initializeAccounts();
      },
    });
  }

  private initializeAccounts(): void {
    this.initializing = false;
    this.loadAccounts();
  }

  onAddAccount(): void {
    this.router.navigate(['/account-management/add']);
  }

  onAccountClick(account: Account): void {
    this.accountService.selectAccount(account);
    this.router.navigate(['/account-management', account.id]);
  }

  protected updateFilters(newFilters: Partial<AccountFilters>): void {
    this.accountService.setFilters(newFilters);
    this.currentPage = 0;
    this.loadAccounts();
  }

  private loadAccounts(): void {
    const currentFilters = this.loadFilters();
    this.accountService.getAccounts(currentFilters, this.currentPage).subscribe({
      error: (error) => {
        console.error('Failed to load accounts:', error);
      },
    });
  }

  private loadFilters(): AccountFilters {
    const filters = this.accountService.filters();

    if (filters.search) {
      this.searchTerm = filters.search;
    }

    if (filters.sortBy) {
      const sort = this.sortOptions.find((o) => o.value === filters.sortBy);
      if (sort) {
        this.selectedSortOption = sort.label;
      }
    }

    if (filters.status) {
      const status = this.statusOptions.find((o) => o.value === filters.status);
      if (status) {
        this.selectedStatusOption = status.label;
      }
    }

    return filters;
  }

  private listenRouteLeave(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationStart) => {
        if (!event.url.startsWith('/account-management')) {
          this.accountService.clearFilters();
        }
      });
  }

  closeAllDropdowns(): void {
    this.openDropdown = null;
  }

  get groupNames(): string {
    return this.currentUser?.groups?.map((g: Group) => g.name).join(' / ') || '';
  }

  get statusClass(): string {
    return this.currentUser?.status === AccountStatus.ACTIVE ? 'active' : 'inactive';
  }
}
